<template>
  <div class="app-container">
    <Container
      :title="t('route.acceptanceHistory')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :hasCreate="false /*$hasPermission(['approvalform_M'])*/"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          @submit="submitSearch"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <el-row>
          <el-col
            :span="12"
          >
            <el-button
              type="primary"
              size="large"
              style="margin-bottom: 10px"
              @click="handleExport"
            >
              {{ t('common.exportExcel') }}
            </el-button>
          </el-col>
        </el-row>
        <SimpleTable
          ref="tableRef"
          :listLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
          :hadSelect="false"
          :operateColumnWidth="/*$hasPermission(['approvalform_M'])*/ true ? 240:80"
        >
          <template
            #default="slotProps"
          >
            <el-button
              @click="viewDetail(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('Acceptance.viewDetails') }}
            </el-button>
          </template>
        </SimpleTable>
        <el-pagination
          :total="total"
          v-show="total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100, 200]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import SearchForm from '../../../components/form/search/SearchForm.vue'
import SimpleTable from '../../../components/table/simple/SimpleTable.vue'
import Container from '../../../components/form/container/Container.vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { formatDate, formatThousands, toMaximumTimeOfDay } from '@/utils'
import { createModel } from '@/utils/component'
import { exportProcessed, getProcessedPagelist } from '@/apis/accepted-bill/acceptanceReceipt'
import {
  ProcessedAcceptanceHistoryModel,
  ProcessedAcceptancePageParamModel
} from '@/model/accepted-bill/acceptedBillModel'
import dayjs from 'dayjs'
import { ElMessage } from 'element-plus'
import { formatDraftType } from '@/utils/acceptanceBillFormatUtil'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'

export default defineComponent({
  name: 'AcceptanceHistoryPageList',
  components: {
    SearchForm,
    SimpleTable,
    Container
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const tableRef = ref()
    const paramModelRef = ref<ProcessedAcceptancePageParamModel | null>(null)
    const statusOptions = [
      { label: t('acceptanceStatus.pendingConfirmation'), value: 0 },
      { label: t('acceptanceStatus.underReview'), value: 1 },
      { label: t('acceptanceStatus.pendingAction'), value: 2 },
      { label: t('acceptanceStatus.operated'), value: 3 },
      { label: t('acceptanceStatus.received'), value: 98 },
      { label: t('acceptanceStatus.rejected'), value: 99 }
    ]
    const searchStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      { prop: 'requestObject', label: t('Acceptance.requestObject'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'dateRange',
        label: t('Acceptance.requestTime'),
        component: 'el-date-picker',
        type: 'daterange',
        model: []
      },
      { prop: 'draftNo', label: t('Acceptance.draftNo'), component: 'el-input', type: 'text', model: '' }
    ]
    const approveAcceptanceTableStruct = [
      { prop: 'id', label: t('Acceptance.id'), width: 80 },
      { prop: 'companyCode', label: t('company.companyCode'), width: 100 },
      { prop: 'requestObject', label: t('Acceptance.requestObject') },
      { prop: 'draftNo', label: t('Acceptance.draftNo') },
      {
        prop: 'requestDate',
        label: t('Acceptance.requestDate'),
        formatter: (row: ProcessedAcceptanceHistoryModel) => formatDate(row.requestDate, 'yyyy-MM-dd')
      },
      {
        prop: 'issueDate',
        label: t('Acceptance.issueDate'),
        formatter: (row: ProcessedAcceptanceHistoryModel) => formatDate(row.issueDate, 'yyyy-MM-dd')
      },
      {
        prop: 'dueDate',
        label: t('Acceptance.dueDate'),
        formatter: (row: ProcessedAcceptanceHistoryModel) => formatDate(row.dueDate, 'yyyy-MM-dd')
      },
      {
        prop: 'draftType',
        label: t('Acceptance.draftType'),
        formatter: (row: ProcessedAcceptanceHistoryModel) => formatDraftType(row.draftType)
      },
      { prop: 'bankName', label: t('Acceptance.bankName') },
      {
        prop: 'draftAmt',
        label: t('Acceptance.draftAmt'),
        formatter: (row: any) => formatThousands(row.draftAmt),
        align: 'right'
      },
      {
        prop: 'status',
        label: t('Acceptance.status'),
        formatter: (row: ProcessedAcceptanceHistoryModel) => {
          return statusOptions.find(item => item.value === row.status)?.label
        }
      }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as any,
      tableData: Array<ProcessedAcceptanceHistoryModel>(),
      columnStruct: approveAcceptanceTableStruct,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true

        let requestDateStart = null as string | null
        let requestDateEnd = null as string | null
        if (dataMap.searchFormModel.dateRange && dataMap.searchFormModel.dateRange.length > 1) {
          requestDateStart = dataMap.searchFormModel.dateRange[0] ? dayjs(dataMap.searchFormModel.dateRange[0])
            .format('YYYY-MM-DD HH:mm:ss') : null
          requestDateEnd = dataMap.searchFormModel.dateRange[1] ? dayjs(toMaximumTimeOfDay(dataMap.searchFormModel.dateRange[1]))
            .format('YYYY-MM-DD HH:mm:ss') : null
        }

        const params: ProcessedAcceptancePageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          requestObject: dataMap.searchFormModel.requestObject,
          requestDateStart: requestDateStart,
          requestDateEnd: requestDateEnd,
          draftNo: dataMap.searchFormModel.draftNo,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getProcessedPagelist(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
          if (data?.code !== 200 && data?.msg) {
            ElMessage({
              message: data.msg,
              type: 'error',
              duration: 2000
            })
          }
        }
        dataMap.total = data?.data?.totalCount ?? 0
        paramModelRef.value = params
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      },
      async handleExport() {
        if (!paramModelRef.value) {
          alert('请先进行检索')
          return
        }
        const params: ProcessedAcceptancePageParamModel = {
          companyCode: paramModelRef.value.companyCode,
          requestObject: paramModelRef.value.requestObject,
          requestDateStart: paramModelRef.value.requestDateStart,
          requestDateEnd: paramModelRef.value.requestDateEnd,
          draftNo: paramModelRef.value.draftNo,
          pageCurrent: paramModelRef.value.pageCurrent,
          pageSize: paramModelRef.value.pageSize
        }
        const urlBlob = await exportProcessed(params)
        if (urlBlob) {
          const link = document.createElement('a')
          link.href = urlBlob
          link.download = '历史承兑票.xlsx'
          link.click()
          window.URL.revokeObjectURL(urlBlob)
        } else {
          ElMessage({
            message: '获取excel失败',
            type: 'error',
            duration: 2000
          })
        }
      }
    })
    const viewDetail = (row: ProcessedAcceptanceHistoryModel) => {
      router.push({ path: `acceptanceHistoryDetail/${row.id}` })
    }
    const getCompanyList = async() => {
      dataMap.searchFormStruct[0].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[0].options = optionList
      dataMap.searchFormStruct[0].loading = false
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    return {
      t,
      tableRef,
      viewDetail,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style scoped>

</style>
